import { defineStore } from "pinia";
import { computed } from "vue";

/**
 * Simple Pinia store to manage whether the current rendering context is for PDF output.
 * This replaces scattered DOM class sniffing (`body.forPdf`) for JS logic in components
 * and composables, while we continue to add the "forPdf" class to <body> for CSS rules.
 *
 * Usage in components:
 *   import { useIsInPdfMode } from "~/stores/pdfModeStore";
 *   const inPdf = useIsInPdfMode();
 *
 * The WorkflowPdfViewer (and any future dedicated PDF rendering entry points) is
 * responsible for calling enterPdfMode() / exitPdfMode().
 */
export const usePdfModeStore = defineStore("pdfModeStore", {
  state: () => ({
    isPdfMode: false,
  }),
  actions: {
    enterPdfMode() {
      if (!this.isPdfMode) {
        this.isPdfMode = true;
        if (typeof document !== "undefined") {
          document.body.classList.add("forPdf");
        }
      }
    },
    exitPdfMode() {
      if (this.isPdfMode) {
        this.isPdfMode = false;
        if (typeof document !== "undefined") {
          document.body.classList.remove("forPdf");
        }
      }
    },
  },
  getters: {
    forPdf: (state) => state.isPdfMode,
  },
});

/**
 * Convenience composable that returns a reactive boolean indicating PDF mode.
 * Replaces the previous pattern of:
 *   const inPdf = computed(() => document.querySelector("body")?.classList.contains("forPdf"));
 */
export function useIsInPdfMode() {
  const pdfModeStore = usePdfModeStore();
  return computed(() => pdfModeStore.isPdfMode);
}
